import { existsSync, readFileSync, writeFileSync } from "node:fs";

import { bigintReplacer, bigintReviver } from "@gotts.ai/testnet";

import { deployERC8004 } from "./erc8004";
import { deployFoundation } from "./foundation";
import { mergeHooks } from "./hooks";
import { deployUniswapX } from "./uniswapx";
import { deployUniversalRouter } from "./universal-router";
import { deployV2 } from "./v2";
import { deployV3 } from "./v3";
import { deployV4 } from "./v4";
import { deployVaultCore } from "./vault";

import type { DeployContext } from "./deploy-utils";
import type { DeploymentHooks } from "./hooks";
import type { DeploymentPhaseResult, UniswapDeployment } from "./types";
import type { Address, PublicClient, WalletClient } from "viem";

export interface DeployOptions {
  publicClient: PublicClient;
  walletClient: WalletClient;
  deployer: Address;
  /** Low-level RPC request function for Anvil-specific methods */
  request: (args: { method: string; params: unknown[] }) => Promise<unknown>;
  /** Deployment hooks for lifecycle events */
  hooks?: DeploymentHooks;
  /** Path to save deployment addresses (default: .devenv-deployment.json) */
  deploymentPath?: string;
}

/**
 * Deploy the complete Uniswap stack in 8 sequential phases.
 *
 * Each phase deploys a layer of the Uniswap protocol:
 *   1. Foundation (WETH9, Permit2)
 *   2. Uniswap V2 (Factory, Router, Pair hash)
 *   3. Uniswap V3 (Factory, Router, Quoter, NFT PM, Staker, etc.)
 *   4. Uniswap V4 (PoolManager, PositionManager, Quoter, etc.)
 *   5. Universal Router (aggregated routing)
 *   6. UniswapX (Dutch auction reactors)
 *   7. ERC-8004 (Identity + Reputation registries)
 *   8. Vault Core (AgentVaultFactory, FeeModule, etc.)
 *
 * Phase outputs feed into subsequent phases where needed (e.g., WETH9
 * from Phase 1 is used by V2 Router in Phase 2).
 *
 * @param options - Clients, deployer address, hooks, and config
 * @returns Complete deployment manifest with all addresses
 */
export async function deployUniswapStack(
  options: DeployOptions,
): Promise<UniswapDeployment> {
  const hooks = mergeHooks(options.hooks);
  const deploymentPath = options.deploymentPath ?? ".devenv-deployment.json";

  const ctx: DeployContext = {
    publicClient: options.publicClient,
    walletClient: options.walletClient,
    deployer: options.deployer,
    request: options.request,
  };

  const chainId = await options.publicClient.getChainId();
  const rpcUrl =
    (options.publicClient.transport as { url?: string }).url ??
    "http://127.0.0.1:8545";

  await hooks.beforeDeploy({ chainId, rpcUrl });

  async function executePhase<T>(
    phase: number,
    fn: () => Promise<DeploymentPhaseResult<T>>,
  ): Promise<DeploymentPhaseResult<T>> {
    try {
      const result = await fn();
      await hooks.afterPhase(result as DeploymentPhaseResult<unknown>);
      return result;
    } catch (err) {
      await hooks.onError(
        phase,
        err instanceof Error ? err : new Error(String(err)),
      );
      throw err;
    }
  }

  const foundation = await executePhase(1, () => deployFoundation(ctx));
  const v2 = await executePhase(2, () => deployV2(ctx, foundation.addresses));
  const v3 = await executePhase(3, () =>
    deployV3(ctx, foundation.addresses, v2.addresses),
  );
  const v4 = await executePhase(4, () => deployV4(ctx, foundation.addresses));
  const ur = await executePhase(5, () =>
    deployUniversalRouter(
      ctx,
      foundation.addresses,
      v2.addresses,
      v3.addresses,
      v4.addresses,
    ),
  );
  const ux = await executePhase(6, () =>
    deployUniswapX(ctx, foundation.addresses),
  );
  const erc8004 = await executePhase(7, () => deployERC8004(ctx));
  const vault = await executePhase(8, () =>
    deployVaultCore(ctx, foundation.addresses, erc8004.addresses),
  );

  // Build the complete deployment manifest
  const block = await options.publicClient.getBlock();
  const deployment: UniswapDeployment = {
    chainId,
    deployer: options.deployer,
    blockNumber: Number(block.number),
    timestamp: Number(block.timestamp),
    foundation: foundation.addresses,
    v2: v2.addresses,
    v3: v3.addresses,
    v4: v4.addresses,
    universalRouter: ur.addresses,
    uniswapX: ux.addresses,
    erc8004: erc8004.addresses,
    vault: vault.addresses,
  };

  await hooks.afterDeploy(deployment);

  // Persist deployment manifest to disk
  saveDeployment(deployment, deploymentPath);

  return deployment;
}

/**
 * Save a deployment manifest to disk as JSON.
 *
 * Uses `bigintReplacer` from `@gotts.ai/testnet` to serialize BigInt
 * values (e.g., gasUsed) as "123n" strings for round-trip fidelity.
 */
export function saveDeployment(
  deployment: UniswapDeployment,
  path: string,
): void {
  writeFileSync(path, JSON.stringify(deployment, bigintReplacer, 2), "utf-8");
}

/**
 * Load a previously saved deployment manifest from disk.
 *
 * Returns `null` if the file does not exist, allowing callers to
 * check for a cached deployment before re-deploying.
 */
export function loadDeployment(path: string): UniswapDeployment | null {
  if (!existsSync(path)) return null;
  const raw = readFileSync(path, "utf-8");
  return JSON.parse(raw, bigintReviver) as UniswapDeployment;
}
